/**
 * Allows for tables to use keyboard arrow
 * keys (up, down, left, right) to navigate the table.
 *
 * The hook also includes meta + c to copy the content. Since the
 * table is not ADF, this only copies text content and not the
 * full experience like images / status lozenge.
 */
export const useKeyboardNavigation = () => {
	const tableNavigationControllers = (event: React.KeyboardEvent<HTMLTableCellElement>) => {
		const key = event.key;
		const target = event.currentTarget;
		if (
			key === 'ArrowDown' ||
			key === 'ArrowUp' ||
			key === 'ArrowLeft' ||
			key === 'ArrowRight' ||
			(key === 'c' && event.metaKey)
		) {
			event.preventDefault();
		} else {
			return;
		}
		const getFocusableElement = (element: Element): HTMLElement | null => {
			const focusableSelector = 'input, select, textarea, button, [tabindex]:not([tabindex="-1"])';
			return element.querySelector(focusableSelector);
		};
		let nextElement: Element | undefined | null;
		switch (key) {
			case 'ArrowDown': {
				nextElement = target.parentElement?.nextElementSibling?.children[target.cellIndex];
				break;
			}
			case 'ArrowUp': {
				nextElement = target.parentElement?.previousElementSibling?.children[target.cellIndex];
				break;
			}
			case 'ArrowLeft': {
				nextElement = target.previousElementSibling;
				break;
			}
			case 'ArrowRight': {
				nextElement = target.nextElementSibling;
				break;
			}
			case 'c': {
				if (event.metaKey) {
					target.textContent && void navigator.clipboard.writeText(target.textContent);
				}
				break;
			}
		}
		if (nextElement instanceof HTMLElement) {
			nextElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
			const focusableElement = getFocusableElement(nextElement);
			if (focusableElement) {
				focusableElement.focus();
			} else {
				nextElement.focus();
			}
		}
	};

	return {
		tableNavigationControllers,
	};
};
